import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { GripVertical, ChevronUp, ChevronDown, Save } from "lucide-react";

export default function OrderManager({ title, subtitle, flag, field, excludeIds = [] }) {
  const [items, setItems] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/dishes?all=true").then((r) => {
      const list = r.data
        .filter((d) => d[flag] && !excludeIds.includes(d.id))
        .sort((a, b) => (a[field] ?? 999) - (b[field] ?? 999));
      setItems(list);
    }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [flag, field]);

  const move = (from, to) => {
    if (to < 0 || to >= items.length) return;
    setItems((arr) => {
      const next = [...arr];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };

  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    move(dragIdx, idx);
    setDragIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/dishes/reorder", { field, ids: items.map((d) => d.id) });
      toast.success("Ordine salvato");
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-brass/15 p-5" data-testid={`ordermanager-${field}`}>
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-serif text-xl text-bordeaux">{title}</h3>
        <Button onClick={save} disabled={saving} size="sm" className="bg-bordeaux hover:bg-bordeaux-dark text-cream shrink-0" data-testid={`order-save-${field}`}>
          <Save className="w-4 h-4 mr-1" />{saving ? "Salvataggio…" : "Salva ordine"}
        </Button>
      </div>
      {subtitle && <p className="text-xs text-oak/50 mb-4">{subtitle}</p>}
      <div className="space-y-2">
        {items.map((d, i) => (
          <div
            key={d.id}
            draggable
            onDragStart={() => setDragIdx(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(i)}
            onDragEnd={() => setDragIdx(null)}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${dragIdx === i ? "border-bordeaux bg-bordeaux/5 opacity-70" : "border-brass/15 bg-cream-dark/40"}`}
            data-testid={`order-row-${field}-${d.id}`}
          >
            <GripVertical className="w-4 h-4 text-oak/30 cursor-grab shrink-0" />
            <span className="w-5 text-center text-sm text-brass-hover font-serif shrink-0">{i + 1}</span>
            {d.image
              ? <img src={fileUrl(d.image)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
              : <div className="w-10 h-10 rounded bg-brass/10 shrink-0" />}
            <span className="flex-1 min-w-0 truncate text-oak">{d.nome}</span>
            <div className="flex flex-col shrink-0">
              <button onClick={() => move(i, i - 1)} disabled={i === 0} className="text-oak/50 hover:text-bordeaux disabled:opacity-20" data-testid={`order-up-${field}-${d.id}`}><ChevronUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, i + 1)} disabled={i === items.length - 1} className="text-oak/50 hover:text-bordeaux disabled:opacity-20" data-testid={`order-down-${field}-${d.id}`}><ChevronDown className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-oak/50 text-sm py-4 text-center">Nessun piatto.</p>}
      </div>
    </div>
  );
}
